import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonSecondary,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';

import { scrollToSection, WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

import { VENUE } from './kit';

export const WORKSHOP_FAQS = [
  {
    question: 'Do I need any experience with AI?',
    answer:
      'No. We start with the basics of generative AI and build up from there, so the workshop suits every level.',
  },
  {
    question: 'How long is a workshop?',
    answer: 'Three hours, and almost all of it is hands-on practice.',
  },
  {
    question: 'What will I learn?',
    answer:
      'Marky Agent, AI agents trained on your own data, image, video and voice tools, transcription, brand voice and workflows. Team workshops focus on the tools your team will use most.',
  },
  {
    question: 'What should I bring?',
    answer:
      'A laptop. We provide the internet connection and set you up on the Unlimited plan for the session.',
  },
  {
    question: 'Where do the workshops take place?',
    answer: `At ${VENUE.name} (${VENUE.address}), a short walk from the MRT. Doors open 15 minutes before the start.`,
  },
  {
    question: 'Can I book a workshop for my team?',
    answer:
      'Yes. Tell us about your team with the form on this page and we’ll plan a private session around your goals.',
  },
  {
    question: 'What do I get after the workshop?',
    answer:
      'The complete slides and guide, plus a place in our community of business owners for ongoing support.',
  },
];

export default function WorkshopsFaq() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: WORKSHOP_FAQS.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-24 sm:py-32'>
      <Head>
        <script
          type='application/ld+json'
          // Static copy from this file: nothing user-supplied goes in here.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading
            size='md'
            title='Questions, answered.'
            lead='Something else on your mind? Send it with the form and we’ll answer it before the session.'
          />
          <a
            href={`#${WORKSHOP_CONTACT_ID}`}
            onClick={scrollToSection}
            className={clsxm(buttonSecondary, 'mt-8')}
          >
            Ask us a question
            <ArrowGlyph className='h-3.5 w-3.5 rotate-90' />
          </a>
        </div>

        <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] lg:col-span-8 dark:divide-white/[0.08] dark:border-white/[0.08]'>
          {WORKSHOP_FAQS.map(({ question, answer }) => (
            <li key={question}>
              <details className='group'>
                <summary
                  className={clsxm(
                    'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                    tone.ink,
                  )}
                >
                  {question}
                  <span
                    aria-hidden='true'
                    className={clsxm(
                      'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                      tone.line,
                    )}
                  >
                    <span className='absolute h-px w-3 bg-current' />
                    <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                  </span>
                </summary>
                <p
                  className={clsxm(
                    'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                    tone.body,
                  )}
                >
                  {answer}
                </p>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
